import { useTranslation } from "react-i18next";
import { IntegrationType } from "@KassaroAB/shared/types/enums/IntegrationType";
import { FormSection } from "../../../components/FormSection";
import { getEffectiveBookkeepingMapping } from "@KassaroAB/shared/utils/bookkeepingDefaultAccounts";
import type { PaymentMethodSoftpay } from "@KassaroAB/shared/types/paymentMethods/paymentMethod";
import { FieldGroupPaymentMethodFields } from "../paymentMethodFieldGroup";
import { usePaymentMethodForm } from "../paymentMethodFormHook";
import {
  PaymentMethodFormContainer,
  type PaymentMethodFormProps,
} from "./PaymentMethodFormContainer";
import { basePaymentMethodValues } from "./basePaymentMethodValues";
import { Fields, IntegrationField, TextField } from "./paymentMethodFields";

export const SoftpayForm = ({
  formId,
  store,
  paymentMethod,
  savedTick,
  onSubmit,
  onDirtyChange,
}: PaymentMethodFormProps<Omit<PaymentMethodSoftpay, "id">>) => {
  const { t } = useTranslation();
  const mergedAccounts = getEffectiveBookkeepingMapping(
    store.country,
    store.bookkeepingDefaultAccounts,
  );
  const form = usePaymentMethodForm({
    defaultValues: {
      ...basePaymentMethodValues(paymentMethod),
      integrationId: paymentMethod.integrationId ?? "",
      softpayStoreId: paymentMethod.softpayStoreId ?? "",
    },
    onSubmit: ({ value }) => onSubmit(value),
  });

  return (
    <PaymentMethodFormContainer
      formId={formId}
      form={form}
      savedTick={savedTick}
      onDirtyChange={onDirtyChange}
    >
      <FieldGroupPaymentMethodFields
        form={form}
        fields={{
          name: "name",
          type: "type",
          bookkeepingAccountId: "bookkeepingAccountId",
          color: "color",
        }}
        defaultAccountNumber={mergedAccounts.businessAccount}
        t={t}
      />
      <FormSection title={t("paymentMethodForm.additionalSettings")}>
        <Fields>
          <form.Field
            name="integrationId"
            validators={{
              onChange: ({ value }) =>
                value ? undefined : t("paymentMethodForm.integrationRequired"),
            }}
          >
            {(field) => (
              <IntegrationField
                label={t("paymentMethodForm.integration")}
                placeholder={t("paymentMethodForm.selectIntegration")}
                chainId={store.chainId}
                integrationTypes={[IntegrationType.SOFTPAY]}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                errors={field.state.meta.errors}
              />
            )}
          </form.Field>
          <form.Field name="softpayStoreId">
            {(field) => (
              <TextField
                label={t("paymentMethodForm.softpay.softpayStore")}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
        </Fields>
      </FormSection>
    </PaymentMethodFormContainer>
  );
};
